import { baseApi } from "@/shared/api";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import type { ApplicationDetailResponse, CreateCandidateDto, CreateCandidateResponse, GetApplicationsResponse, RejectApplicationDto, RejectApplicationResponse, ReviewApplicationDto, ReviewApplicationResponse } from "../types";

export const hiringApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createCandidate: builder.mutation<CreateCandidateResponse, CreateCandidateDto>({
      query: (body) => ({
        url: "/api/hr/invitations/",
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: [{ type: "Applications" as const, id: "LIST" }],
    }),

    getApplications: builder.query<GetApplicationsResponse[], { stage?: string; status?: string }>({
      query: ({ stage, status }) => {
        const params = new URLSearchParams();
        if (stage) params.append("stage", stage);
        if (status) params.append("status", status);

        return {
          url: `/api/hr/applications/?${params.toString()}`,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: [{ type: "Applications" as const, id: "LIST" }],
    }),

    getApplicationDetail: builder.query<ApplicationDetailResponse, number>({
      query: (id) => ({
        url: `/api/hr/applications/${id}/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: (_result, _error, id) => [{ type: "Applications" as const, id }],
    }),

    reviewHiringApplication: builder.mutation<ReviewApplicationResponse, ReviewApplicationDto>({
      query: ({ id, action, notes }) => ({
        url: `/api/hr/applications/${id}/review/`,
        method: "POST",
        body: { action, ...(notes ? { notes } : {}) },
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Applications" as const, id },
        { type: "Applications" as const, id: "LIST" },
        "PendingActions",
      ],
    }),

    rejectHiringApplication: builder.mutation<RejectApplicationResponse, RejectApplicationDto>({
      query: ({ id, rejection_reason }) => ({
        url: `/api/hr/applications/${id}/reject/`,
        method: "POST",
        body: { rejection_reason },
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Applications" as const, id },
        { type: "Applications" as const, id: "LIST" },
        "PendingActions",
      ],
    }),

    /** @deprecated Candidate-stage profile preview. The личный листок is generated only after the worker completes and signs step 2 (see WorkerPersonalSheetPreviewView / GET /api/hr/workers/{id}/preview-personal-sheet/). Kept for backward compatibility; do not surface in new UI. */
    downloadApplicationProfilePreview: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/hr/applications/${id}/preview-profile/`,
        method: "GET",
        responseHandler: fileResponseHandler,
      }),
    }),
  }),
});

export const {
  useCreateCandidateMutation: useHiringCreateCandidateMutation,
  useGetApplicationsQuery: useHiringGetApplicationsQuery,
  useGetApplicationDetailQuery: useHiringGetApplicationDetailQuery,
  useReviewHiringApplicationMutation: useHiringReviewApplicationMutation,
  useRejectHiringApplicationMutation: useHiringRejectApplicationMutation,
  useDownloadApplicationProfilePreviewMutation: useHiringDownloadApplicationProfilePreviewMutation,
} = hiringApi;

